// src/features/players/components/SecurityTab.tsx

import { useState, useMemo, useCallback } from 'react';
import {
  Card, Group, Text, Button, Stack, Box, TextInput, Select, Switch, Divider,
  Badge, ActionIcon, Tooltip, PasswordInput,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { securityFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { PlayerUtils } from '../PlayerUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { GeoFencingSection } from './GeoFencingSection';
import {
  PLAYER_CONSTRAINTS,
  ACCESS_CONTROL_TYPES,
  ACL_GEO_RULE_TYPES,
  ACL_GEO_RULE_LABELS,
  type AclGeoRuleTypeValue,
} from '@/lib/constants/player';
import type { PlayerViewData, PlayerSecurityFormData, IAclRuleData } from '@/lib/types/Player';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

interface SecurityTabProps {
  player: PlayerViewData;
}

export function SecurityTab({ player }: SecurityTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);
  const { selectedCustomer } = useAuth();
  const allowFeed = selectedCustomer?.cfg.quota.features.account.allowFeed === true;

  // Form handles password fields only
  const { control, handleSubmit, formState: { isDirty: formIsDirty, errors }, reset, watch } = useForm<PlayerSecurityFormData>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: PlayerUtils.transformToSecurityForm(player),
  });

  const accessControlType = watch('accessControlType');

  // Domain ACLs — managed as state (not RHF)
  const initialDomainAcls = useMemo(
    () => player.acls.filter(a => a.aclRuleType === 'domain'),
    [player.acls],
  );
  const [domainAcls, setDomainAcls] = useState<IAclRuleData[]>(initialDomainAcls);
  const [domainInput, setDomainInput] = useState('');

  // Geo-fencing ACLs — managed as state per type
  const initialGeoAcls = useMemo(() => partitionGeoAcls(player.acls), [player.acls]);

  const [geoAcls, setGeoAcls] = useState(initialGeoAcls);

  const handleGeoAclsChange = useCallback((ruleType: AclGeoRuleTypeValue, acls: IAclRuleData[]) => {
    setGeoAcls(prev => ({ ...prev, [ruleType]: acls }));
  }, []);

  // Track whether any ACLs have changed. Semantic comparison ONLY
  // (PlayerUtils.sameAclRules): locally-created rule objects and server rows
  // stringify differently for identical data (JS key order), which left the
  // save bar stranded dirty after every successful ACL save.
  const aclsChanged = useMemo(() => {
    const currentAll = [...domainAcls, ...Object.values(geoAcls).flat()];
    return !PlayerUtils.sameAclRules(currentAll, player.acls);
  }, [domainAcls, geoAcls, player.acls]);

  const isDirty = formIsDirty || aclsChanged;

  // Domain management
  const handleAddDomain = () => {
    const value = domainInput.trim().toLowerCase();
    if (!value) return;
    const exists = domainAcls.some(a => a.value.toLowerCase() === value);
    if (exists) {
      notifications.show({ title: 'Duplicate', message: `"${value}" is already in the list`, color: 'yellow' });
      return;
    }
    setDomainAcls(prev => [...prev, { aclRuleType: 'domain' as IAclRuleData['aclRuleType'], isDeny: false, value }]);
    setDomainInput('');
  };

  const handleRemoveDomain = (value: string) => {
    setDomainAcls(prev => prev.filter(a => a.value.toLowerCase() !== value.toLowerCase()));
  };

  // Save merges form + domain ACLs + geo ACLs
  const onSubmit = async (data: PlayerSecurityFormData) => {
    try {
      const allAcls = [
        ...domainAcls,
        ...geoAcls.geo_country,
        ...geoAcls.geo_region,
        ...geoAcls.geo_dma,
        ...geoAcls.geo_postalcode,
      ];
      const req = PlayerUtils.transformSecurityFormToUpdate(data, player, allAcls);
      const updated = await updateMutation.mutateAsync(req);
      reset(data);
      // Reseed local ACL state from the server's echo so the dirty comparison
      // baselines on exactly what the server stored (belt and braces with the
      // semantic compare — covers server-side normalization of rules).
      setDomainAcls(updated.acls.filter((a) => a.aclRuleType === 'domain'));
      setGeoAcls(partitionGeoAcls(updated.acls));
      notifications.show({ title: 'Saved', message: 'Security settings updated', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const handleDiscard = () => {
    reset();
    setDomainAcls(initialDomainAcls);
    setGeoAcls(initialGeoAcls);
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Text fw={600} size="lg">Security & Access</Text>
        <FormSaveBar isDirty={isDirty} isPending={isPending} onDiscard={handleDiscard} />

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            {/* Access Control */}
            <Text fw={600} size="sm" c="dimmed" tt="uppercase">Access Control</Text>
            <AccessControlTypeField control={control} />
            {accessControlType === 'password' && <PasswordField control={control} />}

            {/* Domain Restrictions */}
            <Divider label="Domain Restrictions" labelPosition="left" />
            <Text size="sm" c="dimmed">
              Restrict playback to specific domains. Only the listed domains will be allowed.
            </Text>

            <Group gap="xs" align="flex-end">
              <TextInput
                label="Add Domain"
                placeholder="example.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="light"
                size="lg"
                onClick={handleAddDomain}
                disabled={!domainInput.trim()}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {domainAcls.length > 0 && (
              <Group gap="xs" wrap="wrap">
                {domainAcls.map(acl => (
                  <Badge
                    key={acl.value}
                    variant="light"
                    size="lg"
                    rightSection={
                      <Tooltip label="Remove" openDelay={300}>
                        <ActionIcon variant="transparent" size="xs" onClick={() => handleRemoveDomain(acl.value)}>
                          <IconX size={12} />
                        </ActionIcon>
                      </Tooltip>
                    }
                  >
                    {acl.value}
                  </Badge>
                ))}
              </Group>
            )}

            {domainAcls.length > 0 && (
              <Text size="xs" c="dimmed">
                {domainAcls.length} domain{domainAcls.length !== 1 ? 's' : ''} allowed
              </Text>
            )}

            {/* Geo-Fencing */}
            {allowFeed && (
              <>
                <Divider label="Geo-Fencing" labelPosition="left" />
                <Text size="sm" c="dimmed">
                  Restrict playback by geographic location.
                </Text>

                {ACL_GEO_RULE_TYPES.map(ruleType => (
                  <GeoFencingSection
                    key={ruleType}
                    ruleType={ruleType}
                    acls={geoAcls[ruleType]}
                    onAclsChange={(acls) => handleGeoAclsChange(ruleType, acls)}
                  />
                ))}
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Field components ---

function AccessControlTypeField({ control }: { control: Control<PlayerSecurityFormData> }) {
  const { field } = useController({ control, name: 'accessControlType' });
  return (
    <Select
      label="Access Control"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={[
        { value: 'none', label: 'None (Open Access)' },
        { value: 'password', label: 'Password Protected' },
      ]}
      w={250}
    />
  );
}

function PasswordField({ control }: { control: Control<PlayerSecurityFormData> }) {
  const { field, fieldState } = useController({ control, name: 'password' });
  return (
    <PasswordInput
      label="Password"
      required
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      description={`${field.value.length}/${PLAYER_CONSTRAINTS.PASSWORD_MAX}`}
      maxLength={PLAYER_CONSTRAINTS.PASSWORD_MAX}
      w={300}
    />
  );
}

// --- Helpers ---

function partitionGeoAcls(acls: IAclRuleData[]): Record<AclGeoRuleTypeValue, IAclRuleData[]> {
  const map: Record<AclGeoRuleTypeValue, IAclRuleData[]> = {
    geo_country: [],
    geo_region: [],
    geo_dma: [],
    geo_postalcode: [],
  };
  for (const acl of acls) {
    if (acl.aclRuleType in map) {
      map[acl.aclRuleType as AclGeoRuleTypeValue].push(acl);
    }
  }
  return map;
}
